import classNames from 'classnames';
import { isNil, map } from 'ramda';
import { TariffRowLabel } from './TariffRowLabel';
import { OptionTariffLabel } from './OptionTariffLabel';
import { CcnTag, Icon } from '@fasstech/middle-design-system';
import { TariffRowPrice } from './TariffRowPrice';
import { GridContentsColumn } from '@@viewCommonComponents/grid/GridContentsColumn';
import { GridContentsColumnItem } from '@@viewCommonComponents/grid/GridContentsColumnItem';

export const PricingFormulasColumnCollectiveHealth = ({
  columnIndex,
  getMiddleText,
  tariffStructureLabel,
  detailPricing,
  totalBudgetYearly,
  totalBudgetMonthly,
  totalBudgetCompanyYearly,
  totalBudgetCompanyMonthly,
  demography,
  allOptions,
  formulaLevelLabel,
  beneficiariesEnrollment
}) => {
  const isLabelsColumn = columnIndex === 1;

  return <GridContentsColumn
    columnIndex={columnIndex}
    itemsClassName={classNames(
      'border-x-1 last:border-b-1 last:rounded-b',
      isLabelsColumn && 'px-3.5 last:pb-3.5',
      !isLabelsColumn && 'px-2.5 border-primary-300 last:pb-2.5',
      !isLabelsColumn && 'translate-x-1/2' // Todo : Only if there is only one offer to display
    )}
  >

    {/* Empty headers (labels) or "conforme CCN" */}
    <GridContentsColumnItem
      className={classNames(
        'border-x-0! py-0!',
        !isLabelsColumn && 'px-0!'
      )}
    >
      {!isLabelsColumn &&
        <CcnTag className='mb-2' text={(<span className='flex items-center justify-center'>
          {<Icon name={'check'} strokeClassName='stroke-success'></Icon>} Conforme à la CCN</span>)}
        />
      }
    </GridContentsColumnItem>

    {/* Price structure (labels) or offer name */}
    <GridContentsColumnItem
      className={classNames(
        'border-x-0! py-0!',
        isLabelsColumn && 'flex items-center ',
        !isLabelsColumn && 'px-0!'
      )}
    >
      {isLabelsColumn && tariffStructureLabel && (
        <h5 className="text-lg font-semibold">{tariffStructureLabel}</h5>
      )}
      {!isLabelsColumn &&
        <div className="rounded-t-lg bg-primary-500 text-white text-center font-semibold text-lg flex items-center justify-center py-2">
          {formulaLevelLabel || ''}
        </div>
      }
    </GridContentsColumnItem>

    {/* Offer prices by structure */}
    {detailPricing.map(
      (detail, index) => (
        <GridContentsColumnItem
          key={detail.tariffKey}
          className={classNames(
            index === 0 && 'pt-3.5',
            index % 2 === 0 && '[&>div]:bg-gray-100',
            isLabelsColumn && index === 0 && 'rounded-t border-t-1'
          )}
        >
          {isLabelsColumn ?
            <TariffRowLabel
              label={detail.label}
            />
            :
            <TariffRowPrice
              priceValue={detail.priceValue}
              priceUnit={detail.priceUnit}
            />
          }
        </GridContentsColumnItem>
      )
    )}

    {/* Total budget */}
    {!isNil(totalBudgetYearly) && (
      <GridContentsColumnItem className='py-2.5'>
        <div className={classNames(
          'flex flex-col justify-center py-2 h-full rounded bg-primary-50',
          isLabelsColumn && 'text-primary-800 px-2.5',
          !isLabelsColumn && 'text-primary-700'
        )}>
          {isLabelsColumn ?
            <>
              <div className="font-semibold text-lg">
                {getMiddleText?.(
                  'NIVEAUXGARANTIESETTARIFS_BUDGET_TOTAL_ESTIMATIF_LABEL'
                ) || 'Budget total estimatif'}
              </div>
              <p className="text-sm">
                (
                {getMiddleText?.(
                  'NIVEAUXGARANTIESETTARIFS_BUDGET_TOTAL_MESSAGE_INFO'
                ) || ''}
                )
              </p>
            </>
            :
            <TariffRowPrice
              priceValue={totalBudgetMonthly}
              yearlyPriceValue={totalBudgetYearly}
            />
          }
        </div>
      </GridContentsColumnItem>
    )}

    {/* Employer's part */}
    {!isNil(totalBudgetCompanyYearly) && (
      <GridContentsColumnItem>
        {isLabelsColumn ?
          <div>
            <div
              className='flex items-center gap-1 h-full px-5'
              dangerouslySetInnerHTML={{ // innerHTML needed as the CMS text includes the <b> tag
                __html:
                        getMiddleText?.(
                          'NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_BLOC_TOTAL_LABEL',
                          {
                            employerPercentShare: `${demography.employerPercentShare}%`
                          }
                        ) || `Part employeur (${demography.employerPercentShare}%)`
              }}
            ></div>
            {beneficiariesEnrollment &&
              <div className='flex align-top gap-2 text-sm px-5'>
                <span className='pt-1'>
                  <Icon name={'check'} strokeClassName='stroke-success' size={13}/>
                </span>
                <span>{getMiddleText('NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_AYANT_DROIT_LABEL')}</span>
              </div>
            }
          </div>
          :
          <TariffRowPrice
            priceValue={totalBudgetCompanyMonthly}
            yearlyPriceValue={totalBudgetCompanyYearly}
          />
        }
      </GridContentsColumnItem>
    )}

    {/* Options prices */}
    {allOptions.length > 0 && (
      <>
        <GridContentsColumnItem>
          {isLabelsColumn &&
          <div className="text-lg font-semibold my-3.5">
            {getMiddleText?.('NIVEAUXGARANTIESETTARIFS_OPTIONS_LABEL') ||
                      'Options'}
          </div>
          }
        </GridContentsColumnItem>
        {map((option) => {
          return (
            <>
              <GridContentsColumnItem
                key={option.label}
              >
                {isLabelsColumn &&
                <p className="font-semibold px-5 py-2 h-full flex items-center">
                  {option.label}
                </p>
                }
              </GridContentsColumnItem>
              {option.tariffs?.map(
                (tariff, index) => (
                  <GridContentsColumnItem
                    key={tariff.tariffKey}
                    className={index % 2 === 0 ? '[&>div]:bg-gray-100' : ''}
                  >
                    {isLabelsColumn ?
                      <OptionTariffLabel
                        label={tariff.tariffLabel}
                        tariffKey={tariff.tariffKey}
                      />
                      :
                      <TariffRowPrice
                        priceValue={tariff.amount}
                        priceUnit={tariff.unit}
                      />
                    }
                  </GridContentsColumnItem>
                )
              )}
            </>
          );
        }, allOptions)}
      </>
    )}
  </GridContentsColumn>;
};

export default PricingFormulasColumnCollectiveHealth;
